// src/components/payments/PaymentHistoryTab.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { paymentService } from '../../services/paymentService';
import styles from './PaymentHistoryTab.module.css';

/* ─────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────── */

const STATUS_META = {
  success:   { label: 'Success',  cls: styles.statusSuccess,  icon: '\u2705' },
  initiated: { label: 'Pending',  cls: styles.statusPending,  icon: '\u23F3' },
  failed:    { label: 'Failed',   cls: styles.statusFailed,   icon: '\u274C' },
  refunded:  { label: 'Refunded', cls: styles.statusRefunded, icon: '\u21A9\uFE0F' },
};

const TYPE_META = {
  'Inspection':    { icon: '\uD83D\uDD0D', cls: styles.typeInspection },
  'Booking':       { icon: '\uD83D\uDCC5', cls: styles.typeBooking    },
  'Full Payment':  { icon: '\uD83D\uDED2', cls: styles.typeOrder      },
};

function formatDate(iso) {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatAmount(rupees) {
  return '\u20B9' + Number(rupees).toLocaleString('en-IN');
}

function formatInvoiceId(id) {
  var year = new Date().getFullYear();
  var padded = String(id).padStart(6, '0');
  return 'INV-' + year + '-' + padded;
}

/* ─────────────────────────────────────────────────────────────
   SHARED INVOICE CSS
───────────────────────────────────────────────────────────── */
function getInvoiceCSS(badgeBg, badgeColor) {
  return '*{margin:0;padding:0;box-sizing:border-box}'
    + '@import url(\'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap\');'
    + 'body{font-family:Inter,Segoe UI,sans-serif;background:#f0f4f8;padding:40px;min-height:100vh}'
    + '.page{max-width:720px;margin:0 auto}'
    + '.card{background:#fff;border-radius:20px;overflow:hidden;box-shadow:0 8px 40px rgba(0,0,0,.12);margin-bottom:0}'
    + '.head{background:linear-gradient(135deg,#003566 0%,#001d3d 100%);padding:40px 48px;display:flex;justify-content:space-between;align-items:flex-start;position:relative;overflow:hidden}'
    + '.head::before{content:"";position:absolute;top:-40px;right:-40px;width:200px;height:200px;background:rgba(255,214,10,.07);border-radius:50%}'
    + '.head::after{content:"";position:absolute;bottom:-60px;left:-20px;width:160px;height:160px;background:rgba(255,214,10,.04);border-radius:50%}'
    + '.brand{position:relative;z-index:1}'
    + '.brand-name{color:#FFD60A;font-size:26px;font-weight:900;letter-spacing:-0.5px;line-height:1}'
    + '.brand-sub{color:rgba(255,255,255,.45);font-size:10px;margin-top:5px;letter-spacing:2px;text-transform:uppercase;font-weight:600}'
    + '.inv-meta{text-align:right;position:relative;z-index:1}'
    + '.inv-id{font-size:26px;font-weight:900;color:#FFD60A;line-height:1;letter-spacing:-0.5px}'
    + '.inv-date{color:rgba(255,255,255,.55);font-size:12px;margin-top:6px;font-weight:500}'
    + '.inv-type-chip{display:inline-block;margin-top:8px;background:rgba(255,214,10,.15);border:1px solid rgba(255,214,10,.3);color:#FFD60A;font-size:10px;font-weight:700;padding:3px 10px;border-radius:99px;letter-spacing:1px;text-transform:uppercase}'
    + '.amount-hero{background:linear-gradient(135deg,#f8fafc,#eff6ff);padding:28px 48px;display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #e2e8f0}'
    + '.amount-lbl{font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:1px}'
    + '.amount-val{font-size:38px;font-weight:900;color:#003566;letter-spacing:-1.5px}'
    + '.body{padding:0 48px 40px}'
    + '.section{margin-top:28px}'
    + '.section-head{display:flex;align-items:center;gap:8px;margin-bottom:14px;padding-bottom:10px;border-bottom:1.5px solid #f1f5f9}'
    + '.section-icon{width:28px;height:28px;border-radius:8px;background:#eff6ff;display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0}'
    + '.section-title{font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px}'
    + '.row{display:flex;justify-content:space-between;align-items:flex-start;padding:9px 0;border-bottom:1px solid #f8fafc}'
    + '.row:last-child{border-bottom:none}'
    + '.row .lbl{font-size:12px;color:#64748b;font-weight:500;flex:1}'
    + '.row .val{font-size:12px;color:#0f172a;font-weight:700;text-align:right;flex:1;max-width:60%}'
    + '.row .val.mono{font-family:monospace;font-size:11px;word-break:break-all}'
    + '.amount-table{width:100%;border-collapse:collapse;margin-top:4px}'
    + '.amount-table tr td{padding:8px 0;font-size:13px;border-bottom:1px solid #f1f5f9}'
    + '.amount-table tr:last-child td{border-bottom:none}'
    + '.amount-table .lbl{color:#64748b;font-weight:500}'
    + '.amount-table .val{text-align:right;font-weight:700;color:#0f172a}'
    + '.amount-table .total-row td{padding-top:14px;font-size:15px;font-weight:900;color:#003566;border-top:2px solid #e2e8f0;border-bottom:none}'
    + '.badge{display:inline-block;padding:4px 14px;border-radius:99px;font-size:11px;font-weight:700;background:' + badgeBg + ';color:' + badgeColor + '}'
    + '.timeline{display:flex;flex-direction:column;gap:0}'
    + '.tl-item{display:flex;align-items:flex-start;gap:14px;padding:10px 0}'
    + '.tl-dot-wrap{display:flex;flex-direction:column;align-items:center;flex-shrink:0}'
    + '.tl-dot{width:12px;height:12px;border-radius:50%;background:#003566;flex-shrink:0;margin-top:3px}'
    + '.tl-dot.done{background:#22c55e}'
    + '.tl-dot.pending{background:#f59e0b}'
    + '.tl-line{width:2px;height:24px;background:#e2e8f0;margin-top:2px}'
    + '.tl-item:last-child .tl-line{display:none}'
    + '.tl-content{flex:1}'
    + '.tl-label{font-size:12px;font-weight:700;color:#0f172a}'
    + '.tl-date{font-size:11px;color:#94a3b8;margin-top:2px;font-weight:500}'
    + '.terms{background:#fafbfc;border:1px solid #f1f5f9;border-radius:10px;padding:14px 18px;margin-top:4px}'
    + '.terms ul{list-style:none;display:flex;flex-direction:column;gap:6px}'
    + '.terms li{font-size:11px;color:#64748b;font-weight:500;display:flex;align-items:flex-start;gap:6px}'
    + '.terms li::before{content:"•";color:#003566;font-weight:900;flex-shrink:0}'
    + '.footer{background:#f8fafc;border-top:1.5px solid #f1f5f9;padding:20px 48px;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px}'
    + '.footer p{font-size:11px;color:#94a3b8;font-weight:500}'
    + '.footer .email{font-size:11px;color:#003566;font-weight:700;text-decoration:none}'
    + '.gst-note{font-size:10px;color:#94a3b8;font-style:italic;margin-top:4px}'
    + '.divider{height:1px;background:linear-gradient(90deg,transparent,#e2e8f0,transparent);margin:4px 0}';
}

/* ─────────────────────────────────────────────────────────────
   SECTION BUILDER HELPERS
───────────────────────────────────────────────────────────── */
function sectionHTML(icon, title, rows) {
  var rowsHTML = rows.map(function(r) {
    var valClass = r.mono ? 'val mono' : 'val';
    return '<div class="row"><span class="lbl">' + r.label + '</span><span class="' + valClass + '">' + (r.value || '\u2014') + '</span></div>';
  }).join('');
  return '<div class="section">'
    + '<div class="section-head"><div class="section-icon">' + icon + '</div><span class="section-title">' + title + '</span></div>'
    + rowsHTML
    + '</div>';
}

function amountSectionHTML(rows, totalLabel, totalValue) {
  var rowsHTML = rows.map(function(r) {
    return '<tr><td class="lbl">' + r.label + '</td><td class="val">' + (r.value || '\u2014') + '</td></tr>';
  }).join('');
  return '<div class="section">'
    + '<div class="section-head"><div class="section-icon">&#x1F4B0;</div><span class="section-title">Amount Breakdown</span></div>'
    + '<table class="amount-table"><tbody>'
    + rowsHTML
    + '<tr class="total-row"><td class="lbl">' + totalLabel + '</td><td class="val">' + totalValue + '</td></tr>'
    + '</tbody></table>'
    + '</div>';
}

function termsHTML(lines) {
  var liHTML = lines.map(function(l) { return '<li>' + l + '</li>'; }).join('');
  return '<div class="section">'
    + '<div class="section-head"><div class="section-icon">&#x1F4CB;</div><span class="section-title">Terms &amp; Conditions</span></div>'
    + '<div class="terms"><ul>' + liHTML + '</ul></div>'
    + '</div>';
}

function timelineHTML(items) {
  var itemsHTML = items.map(function(item, i) {
    var dotCls = item.done ? 'tl-dot done' : 'tl-dot pending';
    return '<div class="tl-item">'
      + '<div class="tl-dot-wrap"><div class="' + dotCls + '"></div><div class="tl-line"></div></div>'
      + '<div class="tl-content"><div class="tl-label">' + item.label + '</div><div class="tl-date">' + (item.date || 'Pending') + '</div></div>'
      + '</div>';
  }).join('');
  return '<div class="section">'
    + '<div class="section-head"><div class="section-icon">&#x1F4C5;</div><span class="section-title">Payment Timeline</span></div>'
    + '<div class="timeline">' + itemsHTML + '</div>'
    + '</div>';
}

/* ─────────────────────────────────────────────────────────────
   INVOICE BUILDERS  (one per payment type)
───────────────────────────────────────────────────────────── */

function buildInspectionInvoice(payment) {
  var badgeBg    = payment.status === 'success' ? '#dcfce7' : payment.status === 'failed' ? '#fee2e2' : '#fef9c3';
  var badgeColor = payment.status === 'success' ? '#16a34a' : payment.status === 'failed' ? '#dc2626' : '#ca8a04';
  var statusLabel = payment.status === 'success' ? 'Success' : payment.status === 'failed' ? 'Failed' : payment.status === 'refunded' ? 'Refunded' : 'Pending';

  var customerSection = sectionHTML('&#x1F464;', 'Customer Details', [
    { label: 'Name',  value: payment.user_name  || '\u2014' },
    { label: 'Email', value: payment.user_email || '\u2014' },
    { label: 'Phone', value: payment.user_phone || '\u2014' },
  ]);

  var companySection = sectionHTML('&#x1F3E2;', 'Sold By', [
    { label: 'Company',  value: 'CarYugo' },
    { label: 'GSTIN',    value: 'XXXXXXXX (Pending)' },
    { label: 'Support',  value: 'support@caryugo.com' },
  ]);

  var carSection = sectionHTML('&#x1F697;', 'Car Details', [
    { label: 'Car',     value: payment.car_name || '\u2014' },
    { label: 'Purpose', value: 'Inspection Request' },
  ]);

  var paymentSection = sectionHTML('&#x1F4B3;', 'Payment Details', [
    { label: 'Payment Type',   value: 'Inspection Fee' },
    { label: 'Payment Method', value: 'UPI / Card / NetBanking' },
    { label: 'Payment Status', value: '<span class="badge">' + statusLabel + '</span>' },
  ]);

  var amountSection = amountSectionHTML([
    { label: 'Inspection Fee', value: formatAmount(payment.amount_rupees) },
    { label: 'GST (%)',        value: '\u2014 (Not applicable currently)' },
  ], 'Total Paid', formatAmount(payment.amount_rupees));

  var txnSection = sectionHTML('&#x1F4C4;', 'Transaction Details', [
    { label: 'Razorpay Order ID',   value: payment.razorpay_order_id   || '\u2014', mono: true },
    { label: 'Razorpay Payment ID', value: payment.razorpay_payment_id || '\u2014', mono: true },
  ]);

  var noteSection = '<div class="section">'
    + '<div class="section-head"><div class="section-icon">&#x26A0;&#xFE0F;</div><span class="section-title">Note</span></div>'
    + '<div class="terms"><ul><li>Inspection fee is non-refundable.</li></ul></div>'
    + '</div>';

  return buildInvoicePage(
    formatInvoiceId(payment.id),
    formatDate(payment.created_at),
    'INSPECTION INVOICE',
    formatAmount(payment.amount_rupees),
    badgeBg, badgeColor,
    customerSection + companySection + carSection + paymentSection + amountSection + txnSection + noteSection
  );
}

function buildBookingInvoice(payment) {
  var badgeBg    = payment.status === 'success' ? '#dcfce7' : payment.status === 'failed' ? '#fee2e2' : '#fef9c3';
  var badgeColor = payment.status === 'success' ? '#16a34a' : payment.status === 'failed' ? '#dc2626' : '#ca8a04';
  var statusLabel = payment.status === 'success' ? 'Success' : payment.status === 'failed' ? 'Failed' : payment.status === 'refunded' ? 'Refunded' : 'Pending';

  var customerSection = sectionHTML('&#x1F464;', 'Customer Details', [
    { label: 'Name',  value: payment.user_name  || '\u2014' },
    { label: 'Email', value: payment.user_email || '\u2014' },
    { label: 'Phone', value: payment.user_phone || '\u2014' },
  ]);

  var companySection = sectionHTML('&#x1F3E2;', 'Sold By', [
    { label: 'Company', value: 'CarYugo' },
    { label: 'GSTIN',   value: 'XXXXXXXX (Pending)' },
    { label: 'Support', value: 'support@caryugo.com' },
  ]);

  var carSection = sectionHTML('&#x1F697;', 'Car Details', [
    { label: 'Car',          value: payment.car_name || '\u2014' },
    { label: 'Fuel',         value: payment.car_fuel         || '\u2014' },
    { label: 'Transmission', value: payment.car_transmission || '\u2014' },
  ]);

  var paymentSection = sectionHTML('&#x1F4B3;', 'Payment Details', [
    { label: 'Payment Type',   value: 'Booking Amount' },
    { label: 'Payment Method', value: 'UPI / Card / NetBanking' },
    { label: 'Payment Status', value: '<span class="badge">' + statusLabel + '</span>' },
  ]);

  var amountSection = amountSectionHTML([
    { label: 'Booking Amount', value: formatAmount(payment.amount_rupees) },
    { label: 'GST (%)',        value: '\u2014 (Not applicable currently)' },
  ], 'Total Paid', formatAmount(payment.amount_rupees));

  var txnSection = sectionHTML('&#x1F4C4;', 'Transaction Details', [
    { label: 'Razorpay Order ID',   value: payment.razorpay_order_id   || '\u2014', mono: true },
    { label: 'Razorpay Payment ID', value: payment.razorpay_payment_id || '\u2014', mono: true },
  ]);

  var termsSection = termsHTML([
    'Booking amount is non-refundable.',
    'Car is reserved for a limited time (48\u201372 hours) from booking date.',
    'Full payment must be completed within the reservation period.',
  ]);

  return buildInvoicePage(
    formatInvoiceId(payment.id),
    formatDate(payment.created_at),
    'BOOKING INVOICE',
    formatAmount(payment.amount_rupees),
    badgeBg, badgeColor,
    customerSection + companySection + carSection + paymentSection + amountSection + txnSection + termsSection
  );
}

function buildFullPaymentInvoice(payment) {
  var badgeBg    = payment.status === 'success' ? '#dcfce7' : payment.status === 'failed' ? '#fee2e2' : '#fef9c3';
  var badgeColor = payment.status === 'success' ? '#16a34a' : payment.status === 'failed' ? '#dc2626' : '#ca8a04';
  var statusLabel = payment.status === 'success' ? 'Success' : payment.status === 'failed' ? 'Failed' : payment.status === 'refunded' ? 'Refunded' : 'Pending';

  var customerSection = sectionHTML('&#x1F464;', 'Customer Details', [
    { label: 'Name',    value: payment.user_name    || '\u2014' },
    { label: 'Email',   value: payment.user_email   || '\u2014' },
    { label: 'Phone',   value: payment.user_phone   || '\u2014' },
    { label: 'Address', value: payment.user_address || '\u2014' },
  ]);

  var companySection = sectionHTML('&#x1F3E2;', 'Sold By', [
    { label: 'Company', value: 'CarYugo' },
    { label: 'GSTIN',   value: 'XXXXXXXX (Pending)' },
    { label: 'Address', value: 'Indore, Madhya Pradesh, India' },
    { label: 'Support', value: 'support@caryugo.com' },
  ]);

  var carSection = sectionHTML('&#x1F697;', 'Car Details', [
    { label: 'Car',              value: payment.car_name         || '\u2014' },
    { label: 'Fuel',             value: payment.car_fuel         || '\u2014' },
    { label: 'Transmission',     value: payment.car_transmission || '\u2014' },
    { label: 'Registration No.', value: payment.car_reg          || '\u2014' },
  ]);

  var paymentSection = sectionHTML('&#x1F4B3;', 'Payment Details', [
    { label: 'Payment Type',   value: 'Full Payment' },
    { label: 'Payment Method', value: 'UPI / Card / NetBanking' },
    { label: 'Payment Status', value: '<span class="badge">' + statusLabel + '</span>' },
  ]);

  var amountSection = amountSectionHTML([
    { label: 'Car Price',        value: payment.car_price    ? formatAmount(payment.car_price)    : '\u2014' },
    { label: 'Inspection Fee',   value: payment.insp_fee     ? formatAmount(payment.insp_fee)     : '\u2014' },
    { label: 'Booking Amount',   value: payment.booking_amt  ? formatAmount(payment.booking_amt)  : '\u2014' },
    { label: 'Platform Fee',     value: payment.platform_fee ? formatAmount(payment.platform_fee) : '\u2014' },
    { label: 'GST (%)',          value: '\u2014 (Not applicable currently)' },
  ], 'Total Paid', formatAmount(payment.amount_rupees));

  var tlSection = timelineHTML([
    { label: 'Booking Done',   date: payment.booking_date   || '\u2014', done: !!payment.booking_date   },
    { label: 'Inspection Done',date: payment.inspection_date|| '\u2014', done: !!payment.inspection_date},
    { label: 'Final Payment',  date: formatDate(payment.created_at),     done: payment.status === 'success' },
  ]);

  var txnSection = sectionHTML('&#x1F4C4;', 'Transaction Details', [
    { label: 'Razorpay Order ID',   value: payment.razorpay_order_id   || '\u2014', mono: true },
    { label: 'Razorpay Payment ID', value: payment.razorpay_payment_id || '\u2014', mono: true },
  ]);

  var termsSection = termsHTML([
    'Vehicle ownership transfer will be initiated after full payment confirmation.',
    'Delivery timelines may vary based on location (typically 3\u20137 business days).',
    'Refunds applicable as per company policy \u2014 contact support for queries.',
    'Full payment subject to vehicle transfer process and documentation.',
  ]);

  return buildInvoicePage(
    formatInvoiceId(payment.id),
    formatDate(payment.created_at),
    'FULL PAYMENT INVOICE',
    formatAmount(payment.amount_rupees),
    badgeBg, badgeColor,
    customerSection + companySection + carSection + paymentSection + amountSection + tlSection + txnSection + termsSection
  );
}

/* ─────────────────────────────────────────────────────────────
   PAGE WRAPPER
───────────────────────────────────────────────────────────── */
function buildInvoicePage(invId, invDate, invType, amountStr, badgeBg, badgeColor, bodyContent) {
  return '<!DOCTYPE html><html lang="en"><head>'
    + '<meta charset="UTF-8"/>'
    + '<meta name="viewport" content="width=device-width,initial-scale=1"/>'
    + '<title>' + invId + ' \u2014 CarYugo </title>'
    + '<style>' + getInvoiceCSS(badgeBg, badgeColor) + '</style>'
    + '</head><body>'
    + '<div class="page"><div class="card">'
    +   '<div class="head">'
    +     '<div class="brand"><div class="brand-name">CarYugo</div><div class="brand-sub">Payment Invoice</div></div>'
    +     '<div class="inv-meta">'
    +       '<div class="inv-id">' + invId + '</div>'
    +       '<div class="inv-date">' + invDate + '</div>'
    +       '<div class="inv-type-chip">' + invType + '</div>'
    +     '</div>'
    +   '</div>'
    +   '<div class="amount-hero">'
    +     '<span class="amount-lbl">Amount Paid</span>'
    +     '<span class="amount-val">' + amountStr + '</span>'
    +   '</div>'
    +   '<div class="body">' + bodyContent + '</div>'
    +   '<div class="footer">'
    +     '<p>Thank you for choosing CarYugo</p>'
    +     '<a class="email" href="mailto:support@caryugo.com">support@caryugo.com</a>'
    +   '</div>'
    + '</div></div>'
    + '</body></html>';
}

/* ─────────────────────────────────────────────────────────────
   DOWNLOAD DISPATCHER
───────────────────────────────────────────────────────────── */
function downloadInvoice(payment) {
  var html;
  var type = payment.payment_type_label || '';

  if (type === 'Inspection') {
    html = buildInspectionInvoice(payment);
  } else if (type === 'Booking') {
    html = buildBookingInvoice(payment);
  } else {
    html = buildFullPaymentInvoice(payment);
  }

  var blob = new Blob([html], { type: 'text/html' });
  var url  = URL.createObjectURL(blob);
  var a    = document.createElement('a');
  a.href     = url;
  a.download = 'CarYugo' + formatInvoiceId(payment.id) + '.html';
  a.click();
  URL.revokeObjectURL(url);
}

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
export default function PaymentHistoryTab() {
  const [payments, setPayments] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [filter,   setFilter]   = useState('all');
  const [search,   setSearch]   = useState('');

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await paymentService.getPaymentHistory();
      setPayments(res.data || []);
    } catch (err) {
      setError('Failed to load payment history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function() { fetchHistory(); }, [fetchHistory]);

  const visible = payments.filter(function(p) {
    var matchFilter = filter === 'all' || p.status === filter;
    var q = search.trim().toLowerCase();
    var matchSearch = !q
      || String(p.id).includes(q)
      || (p.car_name || '').toLowerCase().includes(q)
      || (p.payment_type_label || '').toLowerCase().includes(q)
      || (p.razorpay_order_id || '').toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  var counts = payments.reduce(function(acc, p) {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});

  var totalRevenue = payments
    .filter(function(p) { return p.status === 'success'; })
    .reduce(function(s, p) { return s + p.amount_rupees; }, 0);

  var FILTERS = [
    { key: 'all',       label: 'All',      count: payments.length      },
    { key: 'success',   label: 'Success',  count: counts.success   || 0 },
    { key: 'initiated', label: 'Pending',  count: counts.initiated || 0 },
    { key: 'failed',    label: 'Failed',   count: counts.failed    || 0 },
    { key: 'refunded',  label: 'Refunded', count: counts.refunded  || 0 },
  ];

  if (loading) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.loaderWrap}>
          <div className={styles.spinner} />
          <p>Loading payment history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.loaderWrap}>
          <span style={{ fontSize: 48 }}>&#9888;</span>
          <p style={{ color: '#c30000' }}>{error}</p>
          <button className={styles.retryBtn} onClick={fetchHistory}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>

      <div className={styles.tabHeader}>
        <div>
          <h2 className={styles.tabTitle}>Payment History</h2>
          <p className={styles.tabSubtitle}>
            {payments.length} transaction{payments.length !== 1 ? 's' : ''} found
          </p>
        </div>
      </div>

      <div className={styles.summaryGrid}>
        <div className={[styles.summaryCard, styles.summaryTotal].join(' ')}>
          <span className={styles.summaryIcon}>&#x1F4B0;</span>
          <div>
            <div className={styles.summaryValue}>{formatAmount(totalRevenue)}</div>
            <div className={styles.summaryLabel}>Total Paid</div>
          </div>
        </div>
        <div className={[styles.summaryCard, styles.summarySuccess].join(' ')}>
          <span className={styles.summaryIcon}>&#x2705;</span>
          <div>
            <div className={styles.summaryValue}>{counts.success || 0}</div>
            <div className={styles.summaryLabel}>Successful</div>
          </div>
        </div>
        <div className={[styles.summaryCard, styles.summaryPending].join(' ')}>
          <span className={styles.summaryIcon}>&#x23F3;</span>
          <div>
            <div className={styles.summaryValue}>{counts.initiated || 0}</div>
            <div className={styles.summaryLabel}>Pending</div>
          </div>
        </div>
        <div className={[styles.summaryCard, styles.summaryRefunded].join(' ')}>
          <span className={styles.summaryIcon}>&#x21A9;</span>
          <div>
            <div className={styles.summaryValue}>{counts.refunded || 0}</div>
            <div className={styles.summaryLabel}>Refunded</div>
          </div>
        </div>
      </div>

      <div className={styles.toolbar}>
        <input
          type="text"
          className={styles.search}
          placeholder="Search by ID, car, type or order ID..."
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
        />
        <div className={styles.filterRow}>
          {FILTERS.map(function(f) {
            return (
              <button
                key={f.key}
                className={[styles.filterBtn, filter === f.key ? styles.filterActive : ''].join(' ')}
                onClick={function() { setFilter(f.key); }}
              >
                {f.label}
                <span className={styles.filterCount}>{f.count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {visible.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>&#x1F4B3;</div>
          <h3>No payments found</h3>
          <p>
            {payments.length === 0
              ? 'You have not made any payments yet.'
              : 'No payments match your current filters.'}
          </p>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Invoice ID / Payment</th>
                <th>Car</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(function(payment) {
                var sm = STATUS_META[payment.status] || { label: payment.status, cls: '', icon: '\u2022' };
                var tm = TYPE_META[payment.payment_type_label] || { icon: '\uD83D\uDCB3', cls: '' };
                return (
                  <tr key={payment.id} className={styles.tableRow}>

                    <td>
                      <span className={styles.paymentId}>{formatInvoiceId(payment.id)}</span>
                      {payment.razorpay_payment_id && (
                        <div className={styles.subText}>
                          {payment.razorpay_payment_id.slice(0, 18)}...
                        </div>
                      )}
                    </td>

                    <td>
                      <span className={styles.carName}>{payment.car_name || '\u2014'}</span>
                    </td>

                    <td>
                      <span className={[styles.typeBadge, tm.cls || ''].join(' ')}>
                        {tm.icon} {payment.payment_type_label || '\u2014'}
                      </span>
                    </td>

                    <td>
                      <span className={styles.amount}>{formatAmount(payment.amount_rupees)}</span>
                    </td>

                    <td>
                      <span className={[styles.statusBadge, sm.cls || ''].join(' ')}>
                        {sm.icon} {sm.label}
                      </span>
                    </td>

                    <td>
                      <span className={styles.date}>{formatDate(payment.created_at)}</span>
                    </td>

                    <td>
                      <button
                        className={styles.invoiceBtn}
                        onClick={function() { downloadInvoice(payment); }}
                        title="Download invoice"
                      >
                        &#x2B07; Invoice
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}